/**
 * Agent Hub — Overview Tab
 *
 * Full per-bot analytics: Overview stats, Users with variable breakdown,
 * AI Quality charts, and Handoffs — all powered by /analytics/agent-stats
 * and /analytics/agent-variable endpoints.
 */

import React, { useEffect, useState } from 'react'
import {
  MessageCircle,
  MessagesSquare,
  Clock,
  ThumbsUp,
  AlertCircle,
  Users,
  UserCheck,
  UserPlus,
  Brain,
  ArrowRightLeft,
  TrendingUp,
  Zap,
  AlertTriangle,
  Search,
  ChevronLeft,
  ChevronRight,
  BarChart3,
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import { analyticsService } from '../../../services/analytics'
import type {
  AgentStatsResponse,
  VariableBreakdownResponse,
} from '../../../services/analytics'
import type { Chatbot } from '../../../types'
import { Skeleton } from '../../../components/ui/Skeleton'
import { cn } from '../../../utils/cn'
import { logger } from '../../../utils/logger'

interface OverviewTabProps {
  chatbot: Chatbot
}

type SubTab = 'overview' | 'users' | 'ai_quality' | 'handoffs'

// ── Stat card ──────────────────────────────
function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color = 'blue',
}: {
  icon: React.ElementType
  label: string
  value: string | number
  sub?: string
  color?: 'blue' | 'purple' | 'amber' | 'green' | 'red' | 'gray'
}) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 rounded-xl border border-border bg-card">
      <div
        className={cn(
          'w-11 h-11 rounded-xl flex items-center justify-center shrink-0',
          color === 'blue' && 'bg-blue-50 dark:bg-blue-950',
          color === 'purple' && 'bg-purple-50 dark:bg-purple-950',
          color === 'amber' && 'bg-amber-50 dark:bg-amber-950',
          color === 'green' && 'bg-green-50 dark:bg-green-950',
          color === 'red' && 'bg-red-50 dark:bg-red-950',
          color === 'gray' && 'bg-muted',
        )}
      >
        <Icon
          className={cn(
            'w-5 h-5',
            color === 'blue' && 'text-blue-600',
            color === 'purple' && 'text-purple-600',
            color === 'amber' && 'text-amber-600',
            color === 'green' && 'text-green-600',
            color === 'red' && 'text-red-600',
            color === 'gray' && 'text-muted-foreground',
          )}
        />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground leading-tight">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

// ── Period selector ────────────────────────
function PeriodSelector({ value, onChange }: { value: number; onChange: (d: number) => void }) {
  const options = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
  ]
  return (
    <div className="flex gap-1">
      {options.map(o => (
        <button
          key={o.days}
          onClick={() => onChange(o.days)}
          className={cn(
            'px-3 py-1 text-xs font-medium rounded-full transition-colors',
            value === o.days
              ? 'bg-foreground text-background'
              : 'bg-muted text-muted-foreground hover:bg-muted/80',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── Main component ─────────────────────────
export const OverviewTab: React.FC<OverviewTabProps> = ({ chatbot }) => {
  const [days, setDays] = useState(30)
  const [subTab, setSubTab] = useState<SubTab>('overview')
  const [data, setData] = useState<AgentStatsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Variable breakdown state
  const [selectedVar, setSelectedVar] = useState<string | null>(null)
  const [varData, setVarData] = useState<VariableBreakdownResponse | null>(null)
  const [varLoading, setVarLoading] = useState(false)
  const [varSearch, setVarSearch] = useState('')
  const [varPage, setVarPage] = useState(1)

  // Fetch agent stats
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const result = await analyticsService.getAgentStats(chatbot.id, days)
        if (!cancelled) setData(result)
      } catch (err) {
        logger.error('Failed to load agent stats', {
          component: 'OverviewTab',
          error: err instanceof Error ? err.message : 'Unknown',
        })
        if (!cancelled) setError('Failed to load analytics data.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [chatbot.id, days])

  // Reset variable state on period change
  useEffect(() => {
    setSelectedVar(null)
    setVarData(null)
    setVarSearch('')
    setVarPage(1)
  }, [days])

  // Reset page when variable or search changes
  useEffect(() => { setVarPage(1) }, [selectedVar, varSearch])

  // Load variable breakdown on demand (debounced search)
  useEffect(() => {
    if (!selectedVar) return
    let cancelled = false
    const timer = setTimeout(() => {
      setVarLoading(true)
      analyticsService
        .getVariableBreakdown(chatbot.id, selectedVar, days, varPage, 10, varSearch || undefined)
        .then(res => { if (!cancelled) setVarData(res) })
        .catch(() => { if (!cancelled) setVarData(null) })
        .finally(() => { if (!cancelled) setVarLoading(false) })
    }, varSearch ? 300 : 0)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [selectedVar, chatbot.id, days, varPage, varSearch])

  // ── Sub-tab definitions ──
  const subTabs: { id: SubTab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'ai_quality', label: 'AI Quality', icon: Brain },
  ]
  if (data?.handoffs) {
    subTabs.push({ id: 'handoffs', label: 'Handoffs', icon: ArrowRightLeft })
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
    )
  }

  // ── Error ──
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <AlertCircle className="w-6 h-6 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
    )
  }

  if (!data) return null

  // ── Render: Overview ──
  const renderOverview = () => {
    const o = data.overview
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={MessageCircle} label="Conversations" value={o.total_conversations} color="blue" />
          <StatCard icon={MessagesSquare} label="Messages" value={o.total_messages} color="purple" />
          <StatCard
            icon={ThumbsUp}
            label="Satisfaction"
            value={o.satisfaction_rate > 0 ? `${o.satisfaction_rate}%` : '--'}
            sub={o.positive_ratings + o.negative_ratings > 0 ? `${o.positive_ratings} up / ${o.negative_ratings} down` : undefined}
            color="green"
          />
          <StatCard
            icon={Clock}
            label="Avg Response"
            value={o.avg_response_time_ms > 0 ? `${(o.avg_response_time_ms / 1000).toFixed(1)}s` : '--'}
            color="amber"
          />
        </div>

        {/* Daily confidence trend chart */}
        {data.ai_performance.daily_confidence_trend.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h4 className="text-sm font-semibold text-foreground mb-4">Daily AI Confidence</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.ai_performance.daily_confidence_trend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                    tickFormatter={v => v.slice(5)}
                  />
                  <YAxis
                    domain={[0, 1]}
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                    tickFormatter={v => `${(Number(v) * 100).toFixed(0)}%`}
                  />
                  <RechartsTooltip
                    labelFormatter={v => `Date: ${v}`}
                    formatter={(v: number) => [`${(v * 100).toFixed(1)}%`, 'Confidence']}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_confidence"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Usage + Agent details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard
            icon={TrendingUp}
            label="AI Usage"
            value={o.credits_consumed.toLocaleString()}
            sub={data.ai_performance.credits_per_conversation > 0 ? `~${data.ai_performance.credits_per_conversation} per conversation` : undefined}
            color="purple"
          />
          <StatCard
            icon={Zap}
            label="AI Responses"
            value={o.total_ai_responses}
            color="amber"
          />
        </div>

        {/* Agent details summary */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">Agent Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Model</dt>
              <dd className="font-medium text-foreground mt-0.5">
                {chatbot.ai_model?.split('/').pop() || 'Default'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Bot Type</dt>
              <dd className="font-medium text-foreground mt-0.5 capitalize">
                {(chatbot as Record<string, unknown>).bot_type as string || 'custom'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-medium text-foreground mt-0.5">
                {new Date(chatbot.created_at).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd className="font-medium text-foreground mt-0.5">
                {chatbot.is_active ? 'Active' : 'Inactive'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    )
  }

  // ── Render: Users + Variables ──
  const renderUsers = () => {
    const u = data.users
    const varKeys = u.variable_keys || []

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Unique Users" value={u.total_unique_users} color="blue" />
          <StatCard icon={UserCheck} label="Returning" value={u.returning_users} color="green" />
          <StatCard icon={UserPlus} label="New" value={u.new_users} color="purple" />
        </div>

        {/* Peak hours chart */}
        {u.peak_hours.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h4 className="text-sm font-semibold text-foreground mb-4">Peak Usage Hours</h4>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={u.peak_hours}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis
                    dataKey="hour"
                    tick={{ fontSize: 10 }}
                    className="text-muted-foreground"
                    tickFormatter={v => `${v}:00`}
                  />
                  <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" />
                  <RechartsTooltip
                    labelFormatter={v => `${v}:00`}
                    formatter={(v: number) => [v, 'Sessions']}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Variable selector + breakdown */}
        {varKeys.length > 0 ? (
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold text-foreground whitespace-nowrap">
                Break down by
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {varKeys.map(key => (
                  <button
                    key={key}
                    onClick={() => setSelectedVar(prev => prev === key ? null : key)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                      selectedVar === key
                        ? 'bg-foreground text-background'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80',
                    )}
                  >
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            {/* Variable data table */}
            {selectedVar && (
              <div className="rounded-lg border border-border overflow-hidden">
                {/* Header with search */}
                <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center gap-3">
                  <span className="text-xs font-semibold text-foreground shrink-0">
                    {selectedVar.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <div className="relative flex-1 max-w-[200px]">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search values..."
                      value={varSearch}
                      onChange={e => setVarSearch(e.target.value)}
                      className="w-full pl-7 pr-2 py-1 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground"
                    />
                  </div>
                  {varData && (
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">
                      {varData.total} value{varData.total !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {varLoading ? (
                  <div className="p-4 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                    <Skeleton className="h-4 w-3/5" />
                  </div>
                ) : varData && varData.rows.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-background">
                          <tr>
                            <th className="px-3 py-1.5 text-left text-muted-foreground font-medium">Value</th>
                            <th className="px-3 py-1.5 text-right text-muted-foreground font-medium">Sessions</th>
                            <th className="px-3 py-1.5 text-right text-muted-foreground font-medium">Messages</th>
                            <th className="px-3 py-1.5 text-right text-muted-foreground font-medium">Credits</th>
                            <th className="px-3 py-1.5 text-right text-muted-foreground font-medium">Last Seen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {varData.rows.map((r, i) => (
                            <tr key={i} className="border-t border-border hover:bg-muted/30">
                              <td className="px-3 py-2 text-foreground font-medium">
                                <span className="inline-block max-w-[220px] truncate align-bottom" title={r.full_value}>
                                  {r.value}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{r.sessions}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{r.messages}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{r.credits.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground">
                                {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : '--'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {varData.total_pages > 1 && (
                      <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30">
                        <span className="text-xs text-muted-foreground">
                          Page {varData.page} of {varData.total_pages}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setVarPage(p => Math.max(1, p - 1))}
                            disabled={varData.page <= 1}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setVarPage(p => Math.min(varData!.total_pages, p + 1))}
                            disabled={varData.page >= varData.total_pages}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : varData ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {varSearch ? 'No values matching your search.' : 'No data for this variable in the selected period.'}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">No variables configured for this agent.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Pass variables via the widget to segment your users here.
            </p>
          </div>
        )}
      </div>
    )
  }

  // ── Render: AI Quality ──
  const renderAIQuality = () => {
    const ai = data.ai_performance
    const distData = [
      { name: 'High (>80%)', value: ai.confidence_distribution.high, fill: '#10b981' },
      { name: 'Medium (50-80%)', value: ai.confidence_distribution.medium, fill: '#f59e0b' },
      { name: 'Low (<50%)', value: ai.confidence_distribution.low, fill: '#ef4444' },
    ]

    return (
      <div className="space-y-6">
        {/* Confidence distribution */}
        <div className="rounded-xl border border-border bg-card p-6">
          <h4 className="text-sm font-semibold text-foreground mb-4">Confidence Distribution</h4>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" tick={{ fontSize: 10 }} className="text-muted-foreground" />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10 }}
                  className="text-muted-foreground"
                  width={100}
                />
                <RechartsTooltip
                  formatter={(v: number) => [v, 'Responses']}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {distData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Low-confidence messages */}
        {ai.low_confidence_messages.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Low-Confidence Responses
            </h4>
            <div className="overflow-x-auto rounded-lg border border-border max-h-60 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-muted-foreground font-medium">User Query</th>
                    <th className="px-3 py-2 text-left text-muted-foreground font-medium">AI Response</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Conf.</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {ai.low_confidence_messages.map((msg, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 text-foreground max-w-[160px] truncate" title={msg.user_query}>
                        {msg.user_query || '--'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[200px] truncate" title={msg.content}>
                        {msg.content}
                      </td>
                      <td className="px-3 py-2 text-right text-red-600 font-medium">
                        {(msg.confidence * 100).toFixed(0)}%
                      </td>
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleDateString() : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Models breakdown */}
        {ai.models_breakdown.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h4 className="text-sm font-semibold text-foreground mb-3">Models Used</h4>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-muted-foreground font-medium">Model</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Responses</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Credits</th>
                    <th className="px-3 py-2 text-right text-muted-foreground font-medium">Avg Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {ai.models_breakdown.map((m, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 text-foreground font-medium">{m.model}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{m.count}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{(m.credits ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{(m.avg_confidence * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <StatCard icon={Brain} label="Avg Sources / Response" value={ai.avg_sources_used} color="purple" />
      </div>
    )
  }

  // ── Render: Handoffs ──
  const renderHandoffs = () => {
    if (!data.handoffs) return null
    const h = data.handoffs
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={ArrowRightLeft} label="Total Handoffs" value={h.total_handoffs} color="blue" />
          <StatCard icon={TrendingUp} label="Handoff Rate" value={`${h.handoff_rate}%`} color="amber" />
          <StatCard
            icon={Clock}
            label="Avg Wait Time"
            value={h.avg_wait_time_minutes > 0 ? `${h.avg_wait_time_minutes}m` : '--'}
            color="red"
          />
        </div>

        {h.reasons_breakdown.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-6">
            <h4 className="text-sm font-semibold text-foreground mb-3">Handoff Reasons</h4>
            <div className="space-y-1.5">
              {h.reasons_breakdown.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                  <span className="text-sm text-foreground">{r.reason}</span>
                  <span className="text-sm font-medium text-foreground">{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Tab content switch ──
  const renderContent = () => {
    switch (subTab) {
      case 'overview': return renderOverview()
      case 'users': return renderUsers()
      case 'ai_quality': return renderAIQuality()
      case 'handoffs': return renderHandoffs()
      default: return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Sub-tabs + period selector */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {subTabs.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setSubTab(t.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                  subTab === t.id
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            )
          })}
        </div>
        <PeriodSelector value={days} onChange={setDays} />
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  )
}

export default OverviewTab
